import React, { useState, useEffect, useCallback } from 'react';
import { Shield, LogOut, Mail, ShoppingCart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const statusBadge = (status) => {
  const styles = {
    paid: 'bg-green-100 text-green-800 border-green-300',
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    expired: 'bg-gray-100 text-gray-600 border-gray-300',
    refunded: 'bg-blue-100 text-blue-800 border-blue-300',
  };
  return styles[status] || styles.pending;
};

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [purchases, setPurchases] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setEnquiries(null);
    setPurchases(null);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [enq, pur] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, authHeaders()),
        axios.get(`${API}/admin/purchases`, authHeaders()),
      ]);
      setEnquiries(enq.data);
      setPurchases(pur.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Could not load data. Please refresh.');
      }
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (token) loadData();
  }, [token, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#1e3a5f] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-10" data-testid="admin-login-card">
          <div className="text-center mb-8">
            <Shield className="mx-auto mb-4 text-[#dc2626]" size={48} />
            <h1 className="text-2xl font-bold text-[#1e3a5f]">ShieldComply Admin</h1>
            <p className="text-gray-500 mt-2 text-sm">Enter your admin password to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="admin-password" className="text-[#1e3a5f] font-semibold">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                placeholder="Admin password"
              />
            </div>
            <Button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-btn"
              className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold py-5"
            >
              {loggingIn ? 'Logging in...' : 'Log In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#1e3a5f] text-white shadow-md">
        <div className="container mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="text-[#dc2626]" size={28} />
            <h1 className="text-xl font-bold" data-testid="admin-dashboard-title">ShieldComply Admin Dashboard</h1>
          </div>
          <Button
            variant="outline"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1e3a5f]"
          >
            <LogOut size={16} className="mr-2" />
            Log Out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10">
        <Tabs defaultValue="enquiries">
          <TabsList className="mb-6">
            <TabsTrigger value="enquiries" data-testid="enquiries-tab" className="px-6">
              <Mail size={16} className="mr-2" />
              Enquiries {enquiries ? `(${enquiries.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="purchases" data-testid="purchases-tab" className="px-6">
              <ShoppingCart size={16} className="mr-2" />
              Course Purchases {purchases ? `(${purchases.length})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries">
            {!enquiries ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={40} /></div>
            ) : enquiries.length === 0 ? (
              <p className="text-center text-gray-500 py-20" data-testid="no-enquiries-msg">No enquiries yet.</p>
            ) : (
              <div className="space-y-4" data-testid="enquiries-list">
                {enquiries.map((e, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div>
                        <h3 className="font-bold text-[#1e3a5f]">{e.contactName}</h3>
                        <p className="text-sm text-gray-500">{e.businessName}</p>
                      </div>
                      <span className="text-sm text-gray-400">{formatDate(e.submitted_at)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-700">
                      <p><span className="font-semibold">Service:</span> {e.serviceRequired}</p>
                      <p><span className="font-semibold">Email:</span> {e.email}</p>
                      <p><span className="font-semibold">Phone:</span> {e.phone}</p>
                      <p><span className="font-semibold">Address:</span> {e.address}</p>
                    </div>
                    {e.message && (
                      <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded p-3 border border-gray-100">
                        {e.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="purchases">
            {!purchases ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={40} /></div>
            ) : purchases.length === 0 ? (
              <p className="text-center text-gray-500 py-20" data-testid="no-purchases-msg">No course purchases yet.</p>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto" data-testid="purchases-list">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left text-[#1e3a5f]">
                      <th className="px-6 py-4 font-semibold">Course</th>
                      <th className="px-6 py-4 font-semibold">Amount</th>
                      <th className="px-6 py-4 font-semibold">Status</th>
                      <th className="px-6 py-4 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="px-6 py-4 font-medium text-gray-800">{p.course}</td>
                        <td className="px-6 py-4 text-gray-700">${((p.amount || 0) / 100).toFixed(2)} {(p.currency || 'aud').toUpperCase()}</td>
                        <td className="px-6 py-4">
                          <Badge variant="outline" className={statusBadge(p.payment_status)}>{p.payment_status}</Badge>
                        </td>
                        <td className="px-6 py-4 text-gray-500">{formatDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
